import React, { useState } from 'react';
import { Box, Typography, Menu, MenuItem, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import styled from '@mui/material/styles/styled';

// Dropdown Container
const DropdownContainer = styled(Box)(({ theme }) => ({
    backgroundColor: 'rgba(234, 232, 243, 0.2)',
    borderRadius: '10px',
    padding: '5px 10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: 'rgba(234, 232, 243, 0.3)',
    },
}));

// Dropdown Icon
const DropdownIcon = ({ isOpen }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
            }}
        >
            <svg
                width="12"
                height="6"
                viewBox="0 0 12 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M1 1L6 5L11 1"
                    stroke={theme.palette.figma.iconGray || '#9b96a2'}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </Box>
    );
};

// Dropdown Component
const Dropdown = ({
    options = [],
    value = '',
    placeholder,
    onChange = () => { },
    disabled = false,
    fullWidth = false,
    sx = {},
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const displayPlaceholder = placeholder ?? t('all_organizations');
    const [anchorEl, setAnchorEl] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    const handleClick = event => {
        if (!disabled) {
            setAnchorEl(event.currentTarget);
            setIsOpen(true);
        }
    };

    const handleKeyDown = event => {
        if (!disabled && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')) {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
            setIsOpen(true);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setIsOpen(false);
    };

    const handleSelect = option => {
        onChange(option);
        handleClose();
    };

    const selectedOption = options.find(option => option.value === value) || {
        label: displayPlaceholder,
    };
    const getRawLabel = (v) => (typeof v === 'string' ? v : (v && typeof v === 'object' && 'label' in v ? v.label : (v && typeof v === 'object' && 'value' in v ? v.value : String(v ?? ''))));
    /** option.label이 번역 키일 때 화면에 표시할 문구 (Dropdown 내부에서 t 적용) */
    const getDisplayLabel = (label) => (label != null && label !== '' ? t(getRawLabel(label)) : '');

    return (
        <>
            <DropdownContainer
                id="dropdown-button"
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                sx={{
                    width: fullWidth ? '100%' : 'auto',
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    ...sx,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: theme.palette.figma.white[80],
                            letterSpacing: '-0.42px',
                            lineHeight: 'normal',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {getDisplayLabel(selectedOption.label)}
                    </Typography>
                </Box>
                <DropdownIcon isOpen={isOpen} />
            </DropdownContainer>

            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                disableAutoFocusItem={false}
                MenuListProps={{
                    'aria-labelledby': 'dropdown-button',
                }}
                PaperProps={{
                    sx: {
                        backdropFilter: 'blur(10px)',
                        borderRadius: '10px',
                        marginTop: '5px',
                        minWidth: '200px',
                        '& .MuiMenuItem-root': {
                            color: theme.palette.figma.white[80],
                            fontSize: '14px',
                            fontWeight: 500,
                            letterSpacing: '-0.42px',
                            padding: '8px 16px',
                            '&:hover': {
                                backgroundColor: theme.palette.figma.white[20],
                            },
                        },
                    },
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                {options.map((option, index) => (
                    <MenuItem
                        key={option.value !== undefined && option.value !== null ? option.value : `${getRawLabel(option.label)}-${index}`}
                        onClick={() => handleSelect(option)}
                        selected={option.value === value}
                    >
                        <ListItemText primary={getDisplayLabel(option.label)} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default Dropdown;
